// Copyright 2026 FlairCV Inc.
// SPDX-License-Identifier: LicenseRef-FlairCV-Enterprise

import { useMutation } from '@tanstack/react-query';
import { apiClient } from '@/lib/api';
import { useCompleteSignIn } from './useAuth';
import { LoginCodeError, loginCodeErrorMessage } from '../types/login-outcome';

/**
 * Hook for the second login step: exchanges the pending challenge and the
 * emailed code for a session, then finishes the sign-in exactly as a direct
 * login would.
 */
export function useVerifyLoginCode() {
  const completeSignIn = useCompleteSignIn();

  return useMutation({
    mutationFn: async (params: {
      challengeToken: string;
      code: string;
      password: string;
      captchaToken?: string;
    }) => {
      const { data, error, response } = await apiClient.POST('/api/v1/auth/login/verify-code', {
        body: {
          challenge_token: params.challengeToken,
          code: params.code,
          captcha_token: params.captchaToken,
        },
      });

      if (error) {
        throw new LoginCodeError(
          loginCodeErrorMessage(response.status, error.error.message),
          response.status,
        );
      }

      return { data, password: params.password };
    },
    onSuccess: ({ data, password }) => completeSignIn(data, password),
  });
}

/** Hook for emailing a fresh code for a pending login. */
export function useResendLoginCode() {
  return useMutation({
    mutationFn: async (params: { challengeToken: string; captchaToken?: string }) => {
      const { data, error, response } = await apiClient.POST('/api/v1/auth/login/resend-code', {
        body: {
          challenge_token: params.challengeToken,
          captcha_token: params.captchaToken,
        },
      });

      if (error) {
        throw new LoginCodeError(
          loginCodeErrorMessage(response.status, error.error.message),
          response.status,
        );
      }

      return data.data.message;
    },
  });
}
